import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import { SupportService } from '@/app/services/support/support.service';
import {
  SupportTicketStatus,
  SupportTicketSummary,
} from '@/app/common/general-types/support-ticket';
import {
  formatAvgResponseTime,
  formatSupportAdminDateTime,
} from '@/app/common/support-date.util';

@Component({
  selector: 'app-support',
  imports: [RouterLink],
  templateUrl: './support.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SupportComponent implements OnInit, OnDestroy {
  private readonly supportService = inject(SupportService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);

  subscriptionList = new Subscription();

  readonly loading = signal(true);
  readonly tickets = signal<SupportTicketSummary[]>([]);
  readonly activeTab = signal<SupportTicketStatus | 'all'>('open');
  readonly hasAnyTickets = signal(false);
  readonly avgResponseLabel = signal<string | null>(null);
  readonly statsLoading = signal(true);
  readonly formatDateTime = formatSupportAdminDateTime;

  ngOnInit(): void {
    this.supportService.refreshUnreadUserTicketCount();
    this.refreshHasAnyTickets();
    this.loadStats();
    this.loadTickets();
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }

  setTab(tab: SupportTicketStatus | 'all'): void {
    this.activeTab.set(tab);
    this.loadTickets();
  }

  loadTickets(): void {
    this.loading.set(true);
    this.subscriptionList.add(
      this.supportService.listTickets(this.activeTab()).subscribe({
        next: (tickets) => {
          this.tickets.set(tickets);
          if (this.activeTab() === 'all') {
            this.hasAnyTickets.set(tickets.length > 0);
          } else if (tickets.length > 0) {
            this.hasAnyTickets.set(true);
          }
          this.loading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.tickets.set([]);
          this.loading.set(false);
          this.cdr.markForCheck();
        },
      }),
    );
  }

  openTicket(ticketId: string): void {
    this.router.navigate(['/support/tickets', ticketId]);
  }

  statusLabel(status: SupportTicketStatus): string {
    return status === 'open' ? 'Open' : 'Closed';
  }

  emptyStateTitle(): string {
    if (this.activeTab() === 'all') {
      return 'No tickets yet';
    }
    if (this.hasAnyTickets()) {
      return `No ${this.activeTab()} tickets`;
    }
    return `No ${this.activeTab()} tickets yet`;
  }

  newTicketButtonLabel(): string {
    return this.hasAnyTickets() ? 'Open a ticket' : 'Open your first ticket';
  }

  tabClass(tab: SupportTicketStatus | 'all'): string {
    return this.activeTab() === tab
      ? 'rounded-lg bg-white dark:bg-zinc-800 px-4 py-2 text-sm font-semibold text-primary shadow-sm'
      : 'rounded-lg px-4 py-2 text-sm font-medium text-secondary hover:text-primary';
  }

  statusClass(status: SupportTicketStatus): string {
    return status === 'open'
      ? 'bg-green-50 text-green-700 ring-1 ring-green-100 dark:bg-green-900/30 dark:text-green-300'
      : 'bg-gray-100 text-gray-600 dark:bg-zinc-700 dark:text-zinc-300';
  }

  private loadStats(): void {
    this.statsLoading.set(true);
    this.subscriptionList.add(
      this.supportService.getTicketStats('30d').subscribe({
        next: (stats) => {
          const ms = stats?.avgFirstResponseBusinessHoursMs;
          this.avgResponseLabel.set(
            ms != null && ms >= 0 ? formatAvgResponseTime(ms) : null,
          );
          this.statsLoading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.avgResponseLabel.set(null);
          this.statsLoading.set(false);
          this.cdr.markForCheck();
        },
      }),
    );
  }

  private refreshHasAnyTickets(): void {
    this.subscriptionList.add(
      this.supportService.listTickets('all').subscribe({
        next: (tickets) => {
          this.hasAnyTickets.set(tickets.length > 0);
          this.cdr.markForCheck();
        },
        error: () => {
          this.hasAnyTickets.set(false);
          this.cdr.markForCheck();
        },
      }),
    );
  }
}
